import axiosInstance from "../../utils/axios";

const API_BASE_URL = process.env.REACT_APP_API || "";

export class PlantService {
  // Fetch plants based on type (project or org)
  static async fetchPlants(onlyOrg = false) {
    try {
      const response = await axiosInstance.get(
        `${API_BASE_URL}/api/projects/org_plant?only_org=${onlyOrg}`
      );
      return response.data || [];
    } catch (error) {
      console.error("Error fetching plants:", error);
      throw new Error(
        `Failed to fetch ${onlyOrg ? "organization" : "project"} plants`
      );
    }
  }

  // Fetch project plants specifically
  static async fetchProjectPlants() {
    try {
      const response = await axiosInstance.get(
        `${API_BASE_URL}/api/projects/org_plant?only_org=False`
      );
      return response.data || [];
    } catch (error) {
     
      return this.fetchPlants(false);
    }
  }

  // Create new plant
  static async createPlant(plantData) {
    try {
      const payload = this.formatPlantPayload(plantData);

      const response = await axiosInstance.post(
        `${API_BASE_URL}/api/projects/org_plant`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error("Error creating plant:", error);
      throw new Error("Failed to create plant");
    }
  }

  static async updatePlant(plantId, plantData) {
    try {
      const payload = this.formatPlantPayload(plantData);

      try {
        const response = await axiosInstance.post(
          `${API_BASE_URL}/api/projects/org_plant/${plantId}`,
          payload
        );
        return response.data;
      } catch (putError) {
        try {
          const response = await axiosInstance.post(
            `${API_BASE_URL}/api/projects/org_plant?plant_id=${plantId}`,
            payload
          );
          return response.data;
        } catch (postError) {
          // Final fallback to update endpoint
          const response = await axiosInstance.post(
            `${API_BASE_URL}/api/projects/org_plant/update?plant_id=${plantId}`,
            payload
          );
          return response.data;
        }
      }
    } catch (error) {
      console.error("Error updating plant:", error);
      throw new Error("Failed to update plant");
    }
  }

  // Delete plant
  static async deletePlant(plantId) {
    try {
      const response = await axiosInstance.delete(
        `${API_BASE_URL}/api/projects/org_plant?org_plant_id=${plantId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting plant:", error);
      throw new Error("Failed to delete plant");
    }
  }

  // Deallocate plant from project
  static async deallocatePlant(plantProjectId) {
    try {
      const response = await axiosInstance.delete(
        `${API_BASE_URL}/api/projects/plant_project/delete?plant_project_id=${plantProjectId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error deallocating plant:", error);
      throw new Error("Failed to deallocate plant");
    }
  }

  // Import plants to project
  static async importPlants(plantIds) {
    try {
      const orgPlantIds = Array.isArray(plantIds)
        ? plantIds.map(String)
        : [String(plantIds)];
      const payload = { org_plant_ids: orgPlantIds };


      const response = await axiosInstance.post(
        `${API_BASE_URL}/api/projects/plant_project`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error("Error importing plants:", error);
      throw new Error("Failed to import plants");
    }
  }

  static formatPlantPayload(plantData) {
    let documents = [];

    if (plantData.document && Array.isArray(plantData.document)) {
      documents = this.formatDocuments(plantData.document);
    } else if (plantData.documents && Array.isArray(plantData.documents)) {
      documents = this.formatDocuments(plantData.documents);
    }

    if (plantData.profile_image && plantData.profile_image.id) {
      const profileImageDoc = {
        type: 1,
        id: plantData.profile_image.id,
        name: plantData.profile_image.name || "Profile Image",
      };
      documents.unshift(profileImageDoc);
    }

    let dateTimestamp = null;
    if (plantData.date_on_site) {
      if (plantData.date_on_site instanceof Date) {
        dateTimestamp = Math.floor(plantData.date_on_site.getTime() / 1000);
      } else if (typeof plantData.date_on_site === "number") {
        dateTimestamp = plantData.date_on_site > 1000000000000
          ? Math.floor(plantData.date_on_site / 1000)
          : plantData.date_on_site;
      } else if (typeof plantData.date_on_site === "string") {
        const date = new Date(plantData.date_on_site);
        if (!isNaN(date.getTime())) {
          dateTimestamp = Math.floor(date.getTime() / 1000);
        }
      }
    }

    const payload = {
      id: plantData.id,
      name: plantData.nickname || plantData.name || "",
      plant_id: plantData.plant_id || "",
      plant_type: plantData.plant_type || "",
      make: plantData.make || "",
      model: plantData.model || "",
      registration_number: plantData.registration_number || "",
      date: dateTimestamp,
      date_on_site: dateTimestamp,
      document: documents,
      org_id: 1,
      status: 0,
    };

    return payload;
  }

  static formatDocuments(documents) {
    if (!Array.isArray(documents)) {
      console.warn("Documents is not an array:", documents);
      return [];
    }


    const formatted = documents
      .map((doc, index) => {

        // Handle different document structures
        const documentId = doc.id || doc.upload_id || doc.file_id;
        const documentName =
          doc.name || doc.filename || `Document ${index + 1}`;

        if (!documentId) {
          console.warn(`Document ${index} missing ID:`, doc);
          return null;
        }

        let docType = 3;
        if (doc.type) {
          docType = doc.type;
        } else if (documentName) {
          const lowerName = documentName.toLowerCase();
          if (
            lowerName.includes(".jpg") ||
            lowerName.includes(".jpeg") ||
            lowerName.includes(".png") ||
            lowerName.includes(".gif")
          ) {
            docType = 1;
          } else if (
            lowerName.includes("certificate") ||
            lowerName.includes("cert")
          ) {
            docType = 2;
          }
        }

        return {
          type: docType,
          id: documentId,
          name: documentName,
        };
      })
      .filter((doc) => doc !== null);

    return formatted;
  }
}
export class FileService {
  static async uploadFile(file, isOrg = true) {
    try {
      const formData = new FormData();
      formData.append("file", file);


      const response = await axiosInstance.post(
        `${API_BASE_URL}/api/file_upload/upload/image?org=${isOrg}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );


      let fileId = null;
      if (response.data) {
        fileId =
          response.data.upload_id ||
          response.data.id ||
          response.data.file_id ||
          response.data.uploadId ||
          response.data.fileId;

        if (
          !fileId &&
          typeof response.data === "string" &&
          response.data.trim()
        ) {
          fileId = response.data.trim();
        }

        if (!fileId && typeof response.data === "number") {
          fileId = response.data;
        }
      }

      if (!fileId) {
        throw new Error(
          `Upload failed - no ID returned. Response: ${JSON.stringify(
            response.data
          )}`
        );
      }

      return {
        id: fileId,
        upload_id: fileId,
        name: file.name,
        filename: file.name,
        type: file.type.startsWith("image/") ? 1 : 3,
        size: file.size,
      };
    } catch (error) {
      console.error(`Error uploading file ${file.name}:`, error);
      throw new Error(`Failed to upload ${file.name}: ${error.message}`);
    }
  }

  // Get the direct URL (for cases where the API returns a redirect URL)
  static async getFileUrl(fileId, isOrg = true) {
    try {
      const response = await axiosInstance.get(
        `${API_BASE_URL}/api/file_upload/download?id=${fileId}&org=${isOrg}`
      );

      // If the response is a URL string, return it directly
      if (
        typeof response.data === "string" &&
        response.data.startsWith("http")
      ) {
        return response.data;
      }

      if (response.data && response.data.url) {
        return response.data.url;
      }

      return response.data;
    } catch (error) {
      console.error(`Error fetching file URL for ID ${fileId}:`, error);
      return null;
    }
  }

  static async getFileBlob(fileId, isOrg = true) {
    try {

      try {
        const response = await axiosInstance.get(
          `${API_BASE_URL}/api/file_upload/download?id=${fileId}&org=${isOrg}`,
          {
            responseType: "blob",
          }
        );

        if (
          response.data &&
          response.data instanceof Blob &&
          response.data.size > 0
        ) {
          const blobUrl = URL.createObjectURL(response.data);
          return blobUrl;
        }
      } catch (blobError) {
        return blobError.response?.data || null;
      }

      try {
        const urlResponse = await axiosInstance.get(
          `${API_BASE_URL}/api/file_upload/download?id=${fileId}&org=${isOrg}`
        );

        if (
          typeof urlResponse.data === "string" &&
          urlResponse.data.startsWith("http")
        ) {
          return urlResponse.data;
        }

        if (
          urlResponse.data &&
          urlResponse.data.url &&
          urlResponse.data.url.startsWith("http")
        ) {
          return urlResponse.data.url;
        }
      } catch (urlError) {
        console.error("URL request also failed:", urlError);
      }

      console.warn(`Could not retrieve file for ID ${fileId}`);
      return null;
    } catch (error) {
      console.error(`Error fetching file for ID ${fileId}:`, error);
      return null;
    }
  }

  static getImageUrl(fileId, isOrg = true) {
    return `${API_BASE_URL}/api/file_upload/download?id=${fileId}&org=${isOrg}`;
  }

  static async uploadMultipleFiles(files, isOrg = true) {
    const uploadPromises = files.map((file) =>
      this.uploadFile(file, isOrg).catch((error) => ({ error, file }))
    );

    const results = await Promise.all(uploadPromises);

    const successful = results.filter((result) => !result.error);
    const failed = results
      .filter((result) => result.error)
      .map((result) => `${result.file.name}: ${result.error.message}`);

    return { successful, failed };
  }
}
